import React, { useEffect, useRef, useState } from 'react';
import { Button, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { animated } from 'react-spring';
import { AppCriteriaField, AppSkinProps } from '../../types/decisions';
import Image from '../image/Image';
import linesBackground from './linesBackground2.png';

import './draggable.scss';

interface CriteriaItem extends AppCriteriaField {
    backgroundColor: string;
    angle: number;
    translateY: number;
    translateYOffset: number;
    name: string;
    flipped: boolean;
}

interface Props {
    initialCriteriaFields: AppCriteriaField[];
    onCriteriaApply?: () => void;
    onSliderChange: AppSkinProps['onSliderChange'];
    onCriteriaSettingsOpen?: (id: string) => void;
    onAutoPopupCheck?: (newImportance: number, id: string) => void;
    isEditable?: boolean;
}

const colors = [
    '#2E8B57',
    '#1E90FF',
    '#FF6347',
    '#4B0082',
    '#FF4500',
    '#FFB6C1',
    '#3498db',
    '#6B8E23',
    '#800080',
    '#34495E',
];

const CARDS_PER_PAGE = 5;
const anglesAndOffsets: {
    [key: number]: {
        angles: number[],
        offsets: number[]
    }
} = {
    5: {
        angles: [ -18, -9, 0, 9, 18 ],
        offsets: [ 46, 3, -12, 3, 46 ],
    },
    4: {
        angles: [ -15, -5, 5, 15 ],
        offsets: [ 20, -10, -10, 20 ],
    },
    3: {
        angles: [ -10, 0, 10 ],
        offsets: [ 1, -14, 1 ],
    },
    2: {
        angles: [ -5, 5 ],
        offsets: [ -10, -10 ],
    },
    1: {
        angles: [ 0 ],
        offsets: [ -14 ],
    },
};

const generateCriteriaInitialInfo = (initialCriteriaFields: AppCriteriaField[]): CriteriaItem[] => {
    const maxItemsCount = Math.min(initialCriteriaFields.length, 5);
    return initialCriteriaFields.filter(item => !item.hide_importance).map((item, index) => {
        return {
            ...item,
            backgroundColor: colors[index],
            angle: anglesAndOffsets[maxItemsCount].angles[index % CARDS_PER_PAGE],
            name: typeof item.name === 'object' ? item.name.value : item.name,
            translateYOffset: anglesAndOffsets[maxItemsCount].offsets[index % CARDS_PER_PAGE],
            translateY: Math.round(-item.importance * 1.1),
            flipped: false,
        };
    });
};

function getFontSize(textLength: number): number {
    if (textLength < 16) {
        return 18;
    }

    if (textLength < 19) {
        return 16;
    }

    if (textLength < 23) {
        return 14;
    }

    return 12;
}

const DraggableDiv = ({
    initialCriteriaFields,
    onSliderChange,
    onCriteriaApply,
    onCriteriaSettingsOpen,
    onAutoPopupCheck,
    isEditable = true,
}: Props) => {
    const [ dragging, setDragging ] = useState(false);
    const selectedCardIdRef = useRef<string | null>(null);
    const maxDragUp = -110;
    const [ criteria, setCriteria ] = useState<CriteriaItem[]>([]);
    const [ criteriaFirstIndex, setCriteriaFirstIndex ] = useState(0);

    useEffect(() => {
        setCriteria(generateCriteriaInitialInfo(initialCriteriaFields));
    }, [ initialCriteriaFields ]);

    const handleDrag = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!isEditable || !dragging) {
            return;
        }

        if (selectedCardIdRef === null) {
            return;
        }

        const dragAmount = Math.min(0, event.clientY - window.innerHeight + 400);

        if (dragAmount >= maxDragUp) {
            setCriteria((criteria) => {
                return criteria.map((item) => {
                    if (item.id === selectedCardIdRef.current) {
                        return {
                            ...item,
                            importance: Math.abs(dragAmount),
                            translateY: dragAmount,
                        };
                    }

                    return item;
                });
            });
        }
    };

    const handleRelease = () => {
        if (!isEditable) {
            return;
        }

        setDragging(false);
        const currentIndex = criteria.findIndex(item => item.id === selectedCardIdRef.current);
        if (-1 !== currentIndex && selectedCardIdRef.current) {
            const newImportance = Math.min(Math.round(+criteria[currentIndex].importance / 1.09), 100);
            onSliderChange(newImportance, selectedCardIdRef.current);
            onAutoPopupCheck?.(newImportance, selectedCardIdRef.current);
        }
        selectedCardIdRef.current = null;
    };

    const onOpenSettings = (id: string) => {
        if (!isEditable) {
            return;
        }

        onCriteriaSettingsOpen?.(id);
    };

    const criteriaToShow = criteria.slice(criteriaFirstIndex, criteriaFirstIndex + CARDS_PER_PAGE);
    const anglesAnfOffsetsIndex = criteriaToShow.length || 5;
    const { angles, offsets } = anglesAndOffsets[anglesAnfOffsetsIndex];

    return (
        <>
            <div
                onMouseDown={() => setDragging(true)}
                onMouseMove={handleDrag}
                onMouseUp={handleRelease}
                onMouseLeave={handleRelease}
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    backgroundImage: `url(${linesBackground})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                {criteriaToShow.map((criteriaItem, index) => {
                    const isSelected = criteriaItem.id === selectedCardIdRef.current;
                    const transformRotate = `rotate(${angles[index]}deg)`;
                    const transformY = `translateY(${+criteriaItem.translateY + offsets[index]}px)`;
                    const transformValues = `${transformRotate} ${transformY}`;

                    return (
                        <animated.div
                            key={criteriaItem.id}
                            onMouseDown={() => selectedCardIdRef.current = criteriaItem.id}
                            className="app-criteria-card-item"
                            style={{
                                outline: isSelected ? '2px solid black' : '1px solid grey',
                                cursor: isSelected ? 'grab' : 'default',
                                transform: transformValues,
                                // backgroundColor: criteriaItem.backgroundColor,
                                backgroundColor: 'white',
                                zIndex: isSelected ? 2 : 1,
                            }}
                        >
                            <div className="importance-bar" style={{ width: criteriaItem.importance + '%' }}/>
                            <h3
                                className="importance-card-title"
                                style={{
                                    fontSize: getFontSize(criteriaItem.name.length),
                                }}
                            >
                                {criteriaItem.name}
                                {!!onCriteriaSettingsOpen && (
                                    <IconButton style={{ marginLeft: 8 }}
                                                onClick={() => onOpenSettings(criteriaItem.id)}>
                                        <EditIcon color="primary" fontSize="small"/>
                                    </IconButton>
                                )}
                            </h3>
                            <Image src={criteriaItem.icon} className="app-criteria-icon"/>
                        </animated.div>
                    );
                })}
            </div>
            {isEditable && (
                <div className="draggable-div-actions">
                    <Button
                        variant="outlined"
                        disabled={criteriaFirstIndex === 0}
                        onClick={() => setCriteriaFirstIndex(p => p - 1)}
                    >
                        <ChevronLeftIcon/>
                    </Button>
                    {!!onCriteriaApply ? (
                        <Button
                            variant="contained"
                            className="decide-btn"
                            onClick={onCriteriaApply}
                        >
                            Decide
                        </Button>
                    ) : <>&nbsp;</>}
                    <Button
                        variant="outlined"
                        disabled={criteriaFirstIndex > criteria.length - CARDS_PER_PAGE}
                        onClick={() => setCriteriaFirstIndex(p => p + 1)}
                    >
                        <ChevronRightIcon/>
                    </Button>
                </div>
            )}
        </>
    );
};

export default DraggableDiv;